/**
 * V4Inspector — Prove7 Control Vector's right-side node properties panel.
 *
 * Replaces V3's ~5,000-line inline NodePropertiesPanel with a focused,
 * registry-driven design: a small core shell + per-node-type config
 * editors keyed by node.type. Editors register themselves so the shell
 * stays bounded (~300 LOC) and adding support for a new node type is
 * an isolated, low-risk change.
 *
 * Phase 5a-1 (this file's current state) — SKELETON ONLY:
 *   - Right panel renders when a node is selected
 *   - Shows: node label (read-only), node type, node id
 *   - Sentinel-aware footer (no delete on Start/End)
 *   - Empty state when nothing selected
 *
 * Phase 5a-2 will add inline label editing.
 * Phase 5a-3 will add the editor registry + first config editor (http_task).
 * Phase 5a-4+ will add per-node-type editors incrementally.
 *
 * Contract:
 *   selectedNode  Node | null     — the canvas-shape node currently selected
 *                                  (null when nothing is selected)
 *   onUpdate      (id, patch) => void   — host calls canvas.updateNode
 *   onDelete      (id) => void          — host calls canvas.deleteNode
 *                                          (host enforces sentinel guard)
 *   readOnly      boolean              — gates all mutations
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, Info, Trash2, Pencil, ExternalLink } from 'lucide-react';
import EffectiveConfigPanel from '../../components/canvas/EffectiveConfigPanel';
import HttpTaskInspector from './inspectors/HttpTaskInspector';
import LlmTaskInspector from './inspectors/LlmTaskInspector';
import ConditionInspector from './inspectors/ConditionInspector';
import TerminateInspector from './inspectors/TerminateInspector';
import SwitchCaseInspector from './inspectors/SwitchCaseInspector';
import ScriptTaskInspector from './inspectors/ScriptTaskInspector';
import NotificationTaskInspector from './inspectors/NotificationTaskInspector';
import VariableSetInspector from './inspectors/VariableSetInspector';
import ConnectorTaskInspector from './inspectors/ConnectorTaskInspector';
import DataTransformInspector from './inspectors/DataTransformInspector';
import AgentChatInspector from './inspectors/AgentChatInspector';
import HumanTaskInspector from './inspectors/HumanTaskInspector';
import WebhookInspector from './inspectors/WebhookInspector';
import ScheduleInspector from './inspectors/ScheduleInspector';
import ParallelForkInspector from './inspectors/ParallelForkInspector';
import LoopInspector from './inspectors/LoopInspector';
import SubWorkflowInspector from './inspectors/SubWorkflowInspector';

/**
 * NODE_INSPECTOR_REGISTRY — Phase 5a-3+ dispatch map.
 * Add a new entry whenever a new per-node-type editor lands. Anything
 * not in the registry falls through to UnsupportedEditor (which directs
 * the user to V3 for that node's config). This keeps each editor in its
 * own isolated file and makes adding support for a new node type a
 * single-line registry change + a new file.
 *
 * INTENTIONAL ALIASES (Phase 6d audit — keep in sync with
 *   docs/CANVAS_V4_PHASE_6_RELEASE_NOTES.md → "Inspector registry alias docs")
 *
 *   • `approval_gate` → HumanTaskInspector
 *       Pre-form era node. BE TYPE_ALIASES (soarNodes.js:4605) routes
 *       it to the same handler, so the same form-builder UX applies.
 *   • `fork`          → ParallelForkInspector
 *       Conductor FORK_JOIN / FORK_JOIN_DYNAMIC / DYNAMIC_FORK
 *       conversions emit canvas type `fork`. Without the alias,
 *       converted external workflows render with the unsupported-
 *       editor fallback (would be a regression vs V3).
 *   • `end`           → TerminateInspector
 *       Both nodes share { status, message, output } config shape.
 *       Inspector shell still hides the Delete button on type === 'end'
 *       (sentinel guard).
 *
 * If a new alias's config shape diverges from its canonical, write a
 * dedicated Inspector instead — sharing a registry slot to save a
 * file isn't worth the surprise factor downstream.
 */
const NODE_INSPECTOR_REGISTRY = {
  http_task: HttpTaskInspector,
  llm_task: LlmTaskInspector,
  condition: ConditionInspector,
  // 'terminate' (a mid-flow branch-ender added via picker) and 'end'
  // (the workflow's terminal sentinel) share the same config shape —
  // status / message / output. One inspector serves both. The Inspector
  // shell still applies the sentinel guard (no Delete button) when
  // type === 'end' (per SENTINEL_TYPES below).
  terminate: TerminateInspector,
  end: TerminateInspector,
  switch_case: SwitchCaseInspector,
  script_task: ScriptTaskInspector,
  notification_task: NotificationTaskInspector,
  // Phase 6a-1: variable_set — quickest of the data-mutation inspectors,
  // canonical reference for the Phase 6 pattern.
  variable_set: VariableSetInspector,
  // Phase 6a-2: connector_task — bridge to installed connectors. Lazy
  // fetches per-connector skill catalogue + renders typed parameter form.
  connector_task: ConnectorTaskInspector,
  // Phase 6a-3: data_transform — assignments + jq expression editor.
  // jq is the only wired transform type today; the dropdown surfaces
  // the roadmap (jsonpath/jmespath/template/regex) but disables them.
  data_transform: DataTransformInspector,
  // Phase 6a-4: agent_chat — multi-turn agent conversation with skill
  // selection + prompt template + turn/context caps.
  agent_chat: AgentChatInspector,
  // Phase 6a-5: human_task — form-driven Approval Center node. Embeds
  // a mini form-schema builder (text/textarea/select) with per-field
  // required flag and per-select option editor.
  human_task: HumanTaskInspector,
  // approval_gate is a legacy alias of human_task (per soarNodes.js
  // TYPE_ALIASES line 4605) — the BE resolves it to the same handler.
  // Reuse the same Inspector so legacy workflows get the same UX.
  approval_gate: HumanTaskInspector,
  /* RC-152c-1 — form_task gets the same field-builder inspector as
   * human_task. Pre-RC-152c-1 form_task fell through to the schema-
   * driven generic inspector (textarea reading configSchema.formSchema
   * from soarNodes.js), which gave operators raw JSON authoring and
   * NO "Design form ↗" button. The runtime + Approval Center already
   * treat form_task and human_task as the same schema shape (per
   * RC-103: form_task differs from human_task only in formSchema
   * being REQUIRED — same fields[] structure, same renderer); the
   * inspector should mirror the runtime's view and let operators
   * use the same affordance for both. The Design form button (added
   * in RC-152c-1) was never reaching form_task instances until this
   * registry entry landed. */
  form_task: HumanTaskInspector,

  // ── Phase 6b: trigger + control-flow inspectors ──────────────────
  // Phase 6b-1: webhook — trigger via inbound HTTP request, with
  // method/headers/auth validation.
  webhook: WebhookInspector,
  // Phase 6b-2: schedule — cron trigger with timezone + preset chips.
  schedule: ScheduleInspector,
  // Phase 6b-3: parallel_fork — branch row editor. `fork` is the
  // Conductor-compat alias and renders with the same Inspector.
  parallel_fork: ParallelForkInspector,
  fork:          ParallelForkInspector,
  // Phase 6b-4: loop — collection / item / index / maxIterations.
  // Pre-launch single-pass semantics surfaced inline so authors know.
  loop: LoopInspector,
  // Phase 6b-5: sub_workflow — workflow picker with self-exclusion +
  // input payload + timeout + onError policy.
  sub_workflow: SubWorkflowInspector,
  // Phase 6c: polish (mini-map, ⌘K, auto-save, monaco) — no new types.
};

// Sentinel types — Inspector hides the Delete button for these.
// Mirrors the host's sentinel guard so the UI doesn't offer a button
// that would just trigger a "Cannot delete sentinel" toast.
const SENTINEL_TYPES = new Set(['manual_start', 'start', 'end', 'terminate']);

export default function V4Inspector({
  selectedNode,
  onUpdate,
  onDelete,
  onClose,
  readOnly = false,
  /* Phase 7c-3 — when rendered inside V4RightRail, strip the floating
   * chrome (fixed width, left border) so the rail body owns layout. */
  embedded = false,
  /* When the host (V3 CanvasPage "Config" tab) ALREADY supplies the node
   * header, sub-tabs, and a padded scroll container, render ONLY the
   * per-type editor body — no <aside>, no fixed width:320, no nested
   * scroll, no duplicate header/footer. This is what makes the Config tab
   * size like its sibling tabs (Inputs/Output/Attributes) instead of a
   * cramped 320px column inside the already-narrow panel. */
  bodyOnly = false,
  /* RC-152c-1c — Currently-loaded workflow id. Plumbed through from
   * CanvasPageV3.selectedWorkflowId so per-type inspectors that need
   * to deep-link to other surfaces (the HumanTaskInspector's
   * "Design form ↗" button → /forms/design?wid=…) work even when the
   * canvas URL doesn't carry ?workflowId= (e.g. "Recent" / agent-
   * bound navigation paths). Optional: legacy embeddings (tests,
   * slideouts) that don't pass it still work because the inspectors
   * fall back to reading URL searchParams. */
  workflowId = null,
}) {
  if (!selectedNode) {
    return bodyOnly ? null : <EmptyInspector embedded={embedded} />;
  }

  const isSentinel = SENTINEL_TYPES.has(selectedNode.type);
  const canDelete = !readOnly && !isSentinel && typeof onDelete === 'function';
  // PEP-5 — governance node types get the Effective-Config panel below
  // the per-type editor. The panel calls /api/v1/governance/effective-config
  // and renders the resolved tenant + workflow + node-override layers
  // plus the final effective rule set. Anchored to types whose runtime
  // is governance-shaped — DPG scans, policy/autonomy gates, vault
  // operations, audit recorders, compliance scorers. Other node types
  // skip the panel so the Inspector stays lean for non-governance work.
  const GOV_TYPES = new Set([
    'dpg_scan', 'dpg_scan_inbound', 'dpg_scan_outbound', 'dpg_internode_scan',
    'data_classification',
    'policy_enforcement_gate', 'policy_enforcement',
    'agent_autonomy_gate', 'policy_rule_evaluation',
    'token_vault', 'tokenize', 'vault_tokenize',
    'vault_resolve', 'detokenize',
    'audit_record', 'audit_event', 'policy_audit',
    'compliance_scoring', 'compliance_score',
  ]);
  const isGovernanceNode = GOV_TYPES.has(selectedNode.type);

  // bodyOnly — flow the per-type editor directly in the host container.
  if (bodyOnly) {
    const Editor = NODE_INSPECTOR_REGISTRY[selectedNode.type];
    return (
      <>
        {Editor
          ? <Editor node={selectedNode} onUpdate={onUpdate} readOnly={readOnly} workflowId={workflowId} />
          : <UnsupportedEditor node={selectedNode} />}
        {isGovernanceNode && workflowId && (
          <div style={{ marginTop: 12 }}>
            <EffectiveConfigPanel workflowId={workflowId} nodeId={selectedNode.id} />
          </div>
        )}
      </>
    );
  }

  return (
    <aside
      aria-label="Node inspector"
      style={{
        width: embedded ? '100%' : 320,
        flexShrink: 0,
        height: '100%',
        background: '#ffffff',
        borderLeft: embedded ? 'none' : '1px solid #e2e8f0',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header — node name + type + close X */}
      <header style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 8,
        padding: '14px 16px 10px',
        borderBottom: '1px solid #e2e8f0',
        flexShrink: 0,
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontSize: 10, fontWeight: 700,
            color: '#64748b', letterSpacing: 0.6,
            textTransform: 'uppercase', marginBottom: 4,
          }}>
            {prettyType(selectedNode.type)}
          </div>
          {/* Phase 5a-2 — inline label editing.
           * Click the label → flips to a focused input with the text
           * pre-selected. Enter or blur commits (via onUpdate); Escape
           * cancels. Empty / whitespace-only labels are rejected (revert
           * to prior value) — every node needs a recognizable name.
           * Sentinels are read-only (label edit would desync from V3's
           * sentinel renderer which hard-codes "Start"/"End"). */}
          <EditableLabel
            value={selectedNode.label || ''}
            readOnly={readOnly || isSentinel}
            onCommit={(next) => {
              if (typeof onUpdate === 'function') {
                onUpdate(selectedNode.id, { label: next });
              }
            }}
          />
        </div>
        {typeof onClose === 'function' && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close inspector"
            title="Close inspector"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 4, color: '#64748b', borderRadius: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={14} />
          </button>
        )}
      </header>

      {/* Body — Phase 5a-3 registry dispatch. The registered editor for
       * the node's type renders here; unsupported types fall through to
       * UnsupportedEditor (which routes the user to V3 for that node). */}
      <div style={{
        flex: 1, minHeight: 0, overflow: 'auto',
        padding: '14px 16px',
      }}>
        {(() => {
          const Editor = NODE_INSPECTOR_REGISTRY[selectedNode.type];
          if (Editor) {
            return (
              <Editor
                node={selectedNode}
                onUpdate={onUpdate}
                readOnly={readOnly}
                /* RC-152c-1c — same plumb-through as the bodyOnly path. */
                workflowId={workflowId}
              />
            );
          }
          return <UnsupportedEditor node={selectedNode} />;
        })()}
        {/* PEP-5 — Effective-Config panel for governance node types. */}
        {isGovernanceNode && workflowId && (
          <div style={{ marginTop: 12 }}>
            <EffectiveConfigPanel workflowId={workflowId} nodeId={selectedNode.id} />
          </div>
        )}
      </div>

      {/* Footer — node id + Delete (when allowed) */}
      <footer style={{
        padding: '10px 16px',
        borderTop: '1px solid #e2e8f0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8,
        flexShrink: 0,
        background: '#f8fafc',
      }}>
        <code
          title={selectedNode.id}
          style={{
            fontSize: 10, color: '#64748b',
            fontFamily: 'SFMono-Regular, Menlo, monospace',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            maxWidth: 180,
          }}
        >
          {selectedNode.id}
        </code>
        {canDelete && (
          <button
            type="button"
            onClick={() => onDelete(selectedNode.id)}
            title="Delete this node (Backspace)"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px',
              fontSize: 11, fontWeight: 500,
              color: '#991b1b', background: '#fef2f2',
              border: '0.5px solid #fecaca', borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            <Trash2 size={11} /> Delete
          </button>
        )}
        {isSentinel && (
          <span style={{ fontSize: 10, color: '#64748b', fontStyle: 'italic' }}>
            Sentinel — cannot delete
          </span>
        )}
      </footer>
    </aside>
  );
}

/**
 * EditableLabel — Phase 5a-2 inline-editable label.
 * Read-only state shows the label with a discreet pencil icon on hover
 * (signals editability without competing with the label itself). Click
 * flips to an input; Enter / blur commits, Escape reverts.
 *
 * Empty values are rejected — the commit handler reverts the input to
 * the prior value if the user clears the field then commits. The user
 * can still rename to any non-empty string.
 */
function EditableLabel({ value, readOnly, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  // Keep draft in sync when the source value changes (e.g. selection
  // switches to a different node).
  useEffect(() => { setDraft(value); }, [value]);

  // Auto-select all when entering edit mode so a typed character
  // replaces the old name in one keystroke — matches Notion / Linear /
  // every productivity tool's rename UX.
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = (draft || '').trim();
    if (trimmed && trimmed !== value && typeof onCommit === 'function') {
      onCommit(trimmed);
    } else {
      setDraft(value); // revert empty / unchanged
    }
  };
  const cancel = () => { setDraft(value); setEditing(false); };

  if (!editing) {
    return (
      <div
        role={readOnly ? undefined : 'button'}
        tabIndex={readOnly ? undefined : 0}
        onClick={() => { if (!readOnly) setEditing(true); }}
        onKeyDown={(e) => {
          if (readOnly) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(true); }
        }}
        title={readOnly ? value : 'Click to rename'}
        style={{
          fontSize: 15, fontWeight: 600, color: '#0f172a',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          lineHeight: 1.3,
          cursor: readOnly ? 'default' : 'text',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '1px 4px', marginLeft: -4,
          borderRadius: 4,
          maxWidth: '100%',
        }}
        className={readOnly ? undefined : 'v4-editable-label'}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value || '(unnamed)'}
        </span>
        {!readOnly && (
          <Pencil
            size={11}
            aria-hidden="true"
            style={{
              color: '#94a3b8', flexShrink: 0,
              opacity: 0.5,
            }}
          />
        )}
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      }}
      maxLength={120}
      style={{
        width: '100%',
        fontSize: 15, fontWeight: 600, color: '#0f172a',
        lineHeight: 1.3,
        padding: '1px 4px', marginLeft: -4,
        border: '1px solid #14b8a6',
        borderRadius: 4,
        background: '#ffffff',
        outline: 'none',
        fontFamily: 'inherit',
      }}
    />
  );
}

/**
 * EmptyInspector — collapsed right panel when nothing is selected.
 * Stays narrow so the canvas keeps most of the viewport, but visible
 * so the user knows the surface exists.
 */
function EmptyInspector({ embedded = false }) {
  return (
    <aside
      aria-label="Node inspector"
      style={{
        width: embedded ? '100%' : 320,
        flexShrink: 0, height: '100%',
        background: embedded ? '#ffffff' : '#f8fafc',
        borderLeft: embedded ? 'none' : '1px solid #e2e8f0',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, textAlign: 'center',
      }}
    >
      <div style={{ color: '#94a3b8', maxWidth: 220 }}>
        <Info size={20} aria-hidden="true" />
        <div style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
          Select a node on the canvas to inspect and edit its configuration.
        </div>
      </div>
    </aside>
  );
}

/**
 * UnsupportedEditor — fallback when the node's type doesn't have a
 * registered Phase 5a editor yet. Routes the user to V3 for config and
 * shows a transparent diagnostic of what we know about the node so
 * power users can still inspect its shape.
 *
 * As more editors land (Phase 5a-4+), the set of types that fall back
 * here shrinks. Each new editor entry in NODE_INSPECTOR_REGISTRY moves
 * one more type out of this fallback path.
 */
function UnsupportedEditor({ node }) {
  return (
    <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px',
        background: '#fef3c7',
        border: '0.5px solid #fcd34d',
        borderRadius: 4,
        color: '#78350f',
        fontSize: 11, fontWeight: 600,
        marginBottom: 12,
      }}>
        <ExternalLink size={11} aria-hidden="true" />
        Edit in V3 for now
      </div>
      <p style={{ margin: '0 0 12px' }}>
        Configuration editor for <code style={codeStyle}>{node.type}</code> hasn’t
        landed yet (planned across Phase 5a-4+). For now, use the existing
        <strong> Open in V3</strong> link in the top bar to edit this node’s
        config; the canvas auto-syncs the rest.
      </p>
      <div style={{
        marginTop: 16, padding: 10,
        background: '#f1f5f9',
        borderRadius: 6,
        fontSize: 11,
        color: '#0f172a',
      }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Inspector probe</div>
        <KV k="type" v={node.type} />
        <KV k="label" v={node.label || '—'} />
        <KV k="x, y" v={`${node.x ?? '—'}, ${node.y ?? '—'}`} />
        <KV k="manualPosition" v={String(!!node.manualPosition)} />
        <KV k="config keys" v={Object.keys(node.config || {}).join(', ') || '(none)'} />
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div style={{
      display: 'flex', gap: 8,
      padding: '2px 0',
      fontFamily: 'SFMono-Regular, Menlo, monospace',
      fontSize: 11,
    }}>
      <span style={{ color: '#64748b', minWidth: 88 }}>{k}</span>
      <span style={{ color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
    </div>
  );
}

const codeStyle = {
  background: '#f1f5f9', padding: '1px 5px', borderRadius: 3,
  fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11,
};

// Human-friendly type label. V3 displays e.g. "http_task" → "HTTP Task".
// Kept lightweight here; richer labels (with category icon) can land in
// Phase 5a-3 alongside the editor registry.
function prettyType(t) {
  if (!t) return 'NODE';
  return t.replace(/_/g, ' ').toUpperCase();
}
